/**
 * Scrolling list of chat messages.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Auto-scrolls to the bottom whenever a new message arrives.
 * Empty state shows a few example questions tailored to the current
 * scope (site-specific vs portfolio-wide).
 */
import React, { useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';

import type { ChatMessage as ChatMessageType, Citation } from '../../types/chat';
import { ChatMessage } from './ChatMessage';

interface Props {
  messages: ChatMessageType[];
  conversationId: string | null;
  canShareWithTeam: boolean;
  isWaiting?: boolean;
  /** Site or portfolio-wide — drives the empty-state prompts. */
  scopeLabel: string | null;
  /**
   * Data-aware starter questions resolved by the panel from the active
   * site / portfolio availability. When omitted, falls back to the static
   * scope-default examples below.
   */
  exampleQuestions?: string[];
  onResolveCitation?: (citation: Citation) => void;
  onFeedbackUpdate?: (
    messageId: string,
    update: { feedback?: 'positive' | 'negative'; curatedInsightId?: string },
  ) => void;
  onExampleClick?: (text: string) => void;
}

const SITE_EXAMPLES = [
  'Summarise everything you know about this site.',
  'Why is my LCOE above the NREL ATB reference?',
  'What is my conservation closure error and where does it come from?',
  'Which permits will trigger on this site?',
];

const PORTFOLIO_EXAMPLES = [
  'Summarise the state of my whole portfolio.',
  'Rank my sites by LCOE.',
  'Summarise sim status across the portfolio.',
  'Which sites are closest to permit blockers?',
];

export function ChatMessageList({
  messages,
  conversationId,
  canShareWithTeam,
  isWaiting,
  scopeLabel,
  exampleQuestions,
  onResolveCitation,
  onFeedbackUpdate,
  onExampleClick,
}: Props): React.JSX.Element {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, isWaiting]);

  if (messages.length === 0) {
    const fallback = scopeLabel ? SITE_EXAMPLES : PORTFOLIO_EXAMPLES;
    const examples = exampleQuestions && exampleQuestions.length > 0 ? exampleQuestions : fallback;
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center p-6 gap-4 text-center"
        data-testid="chat-empty-state"
      >
        <div className="w-12 h-12 border-2 border-primary/30 bg-primary/5 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <h3 className="text-[10px] uppercase tracking-widest font-bold text-text-primary">
          Ask Datacrate
        </h3>
        <p className="text-[9px] uppercase tracking-widest text-text-muted max-w-sm leading-relaxed">
          Answers reference your evidence + sims. Save the ones you like to
          improve future reports.
        </p>
        <ul className="space-y-2 w-full max-w-sm">
          {examples.map((ex) => (
            <li key={ex}>
              <button
                type="button"
                onClick={() => onExampleClick?.(ex)}
                className="w-full text-left text-[10px] text-text-primary border border-border-subtle px-3 py-2 hover:border-primary hover:bg-primary/5"
                data-testid="chat-example-prompt"
              >
                {ex}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto"
      data-testid="chat-message-list"
    >
      {messages.map((m) => (
        <ChatMessage
          key={m.id}
          message={m}
          conversationId={conversationId ?? ''}
          canShareWithTeam={canShareWithTeam}
          onResolveCitation={onResolveCitation}
          onFeedbackUpdate={onFeedbackUpdate}
        />
      ))}
      {isWaiting && (
        <div className="px-4 py-3 text-[9px] uppercase tracking-widest text-text-muted" data-testid="chat-typing-indicator">
          Datacrate is thinking…
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
